import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import themeColors, { themeFonts} from "@GeriMed/styles/variables";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import { removeCookie } from "@insite/client-framework/Common/Cookies";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
        [fields.fullWidthButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : `${location.pathname}${location.search}`;
    const session = getSession(state);

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl,
        externalError: parsedQuery.externalError,
        isConfirmationPage,
        homePageUrl: getHomePageUrl(state),
        session,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const mapDispatchToProps = {
    signIn,
};

export interface SignInExistingAccountStyles {
    form?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInGridContainer: {
        gap: 8,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            [class*="DescriptionWrapper"],
                            label {
                                display: none;
                            }
                            [class*="FormInputWrapper"] {
                                margin-top: 0px !important;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    signInExistingAccountTitle: {
        variant: "h4",
    },
    userNameGridItem: {
        width: [4, 4, 4, 4, 4],
        css: css`
            input:not(.react-datetime-picker__inputGroup__input) {
                margin: 0;
            }
            @media screen and (min-width: 1280px) {
                flex-basis: initial;
                max-width: initial;
                width: 35%;
            }
        `,
    },
    passwordGridItem: {
        width: [4, 4, 4, 4, 4],
        css: css`
            input:not(.react-datetime-picker__inputGroup__input) {
                margin: 0;
            }
            @media screen and (min-width: 1280px) {
                flex-basis: initial;
                max-width: initial;
                width: 35%;
            }
        `,
    },
    signInButtonGridItem: {
        width: [3, 4, 4, 4, 4],
        css: css`
            button{
                padding: 11px 14px;
            }
            span {
                font-size: 14px;
            }
            @media screen and (min-width: 1280px) {
                flex-basis: initial;
                max-width: initial;
                width: 30%;
            }
        `,
    },
    rememberMeGridItem: {
        width: 12,
        css: css`
            align-items: center;
        `,
    },
    forgotPasswordGridItem: {
        width: 12,
    },
    forgotPasswordLink: {
        typographyProps: {
            size: 14,
            css: css`
                line-height: 24px;
                color: #1c3e95;
                margin: 5px 0;
                &:hover {
                    text-decoration: underline;
                }
            `,
        },
    },
    resetPasswordModal: {
        sizeVariant: "small",
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
    form: {
        css: css`
        &.login-form input:not(.react-datetime-picker__inputGroup__input) {
            margin-bottom: 0 !important;
            font-family: ${themeFonts.fontFamilyLexendDeca};

        }
        `,
    },
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");

const GmSignInExistingAccount = ({
    signIn,
    fields,
    accountSettings,
    returnUrl,
    externalError,
    isConfirmationPage,
    homePageUrl,
    theme,
    session,
}: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || "");

    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    const signInHeaderHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        setErrorMessage("");

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl: theme.signIn?.useOverlay && isConfirmationPage ? homePageUrl : returnUrl,
            skipRedirect: theme.signIn?.useOverlay && !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }

                setErrorMessage(error);
            },
        });
    };

    const submitHeaderHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        removeCookie("PaymentInvoiceList");
        signInHeaderHandler();
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHeaderHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";

    const signInButtonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    const isAuthenticated = !session?.isAuthenticated && !session?.isGuest;

    return (
        <>
            {isAuthenticated && (
                <>
                    <StyledForm className="login-form" {...styles.form} onSubmit={submitHeaderHandler} noValidate>
                        <GridContainer {...styles.signInGridContainer}>
                            <GridItem {...styles.userNameGridItem}>
                                <TextField
                                    id="userName"
                                    {...styles.userNameTextField}
                                    label={
                                        accountSettings.useEmailAsUserName ? translate("Email") : translate("User Name")
                                    }
                                    placeholder="Username"
                                    onChange={e => setUserName(e.currentTarget.value)}
                                    value={userName}
                                    error={userErrorMessage}
                                    autoComplete="username"
                                    data-test-selector="signIn_userName"
                                />
                            </GridItem>
                            <GridItem {...styles.passwordGridItem}>
                                <TextField
                                    id="password"
                                    {...styles.passwordTextField}
                                    label={translate("Password")}
                                    placeholder="Password"
                                    onChange={e => setPassword(e.currentTarget.value)}
                                    value={password}
                                    error={passwordErrorMessage}
                                    // iconProps={{
                                    //     ...styles.icon,
                                    //     src: showPassword ? EyeOff : Eye,
                                    // }}
                                    // iconClickableProps={{
                                    //     onClick: () => {
                                    //         setShowPassword(!showPassword);
                                    //     },
                                    //     type: "button",
                                    // }}
                                    clickableText={password}
                                    autoComplete="current-password"
                                    data-test-selector="signIn_password"
                                    type={showPassword ? "text" : "password"}
                                />
                            </GridItem>
                            <GridItem {...styles.signInButtonGridItem}>
                                <Button
                                    {...styles.signInButton}
                                    css={signInButtonCss}
                                    type="submit"
                                    data-test-selector="signIn_submit"
                                >
                                    {translate("Sign In")}
                                </Button>
                                <Modal
                                    headline={translate("Change Password")}
                                    {...styles.changePasswordModal}
                                    isOpen={isChangePasswordModalOpen}
                                    handleClose={onChangePasswordClose}
                                >
                                    <SignInChangePasswordForm
                                        enteredUserName={userName}
                                        enteredPassword={password}
                                        instructionsText={fields.changePasswordInstructions}
                                        onPasswordChanged={onPasswordChanged}
                                    />
                                </Modal>
                            </GridItem>
                        </GridContainer>
                        <GridItem {...styles.forgotPasswordGridItem}>
                                <Link
                                    {...styles.forgotPasswordLink}
                                    type="button"
                                    onClick={() => setIsResetPasswordModalOpen(true)}
                                    data-test-selector="signIn_forgotPassword"
                                >
                                    {translate("Forgot Password?")}
                                </Link>
                                <Modal
                                    headline={translate("Reset Password")}
                                    {...styles.resetPasswordModal}
                                    isOpen={isResetPasswordModalOpen}
                                    handleClose={() => onResetPasswordClose()}
                                    data-test-selector="resetPasswordModal"
                                >
                                    <SignInResetPasswordForm onClose={() => onResetPasswordClose()} />
                                </Modal>
                        </GridItem>
                    </StyledForm>
                    {userErrorMessage && (
                        <div className="user-error">
                            <span>{userErrorMessage}</span>
                        </div>
                    )}
                    {passwordErrorMessage && (
                        <div className="user-error">
                            <span>{passwordErrorMessage}</span>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(GmSignInExistingAccount)),
    definition: {
        group: "Basic",
        displayName: "Header LogIn",
    },
};
export default widgetModule;
